import { useQuery } from "@apollo/client";
import { LoadingSpinner } from "pepsico-ds";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/common/table/Table";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { getCampaignListing } from "../../../graphql/queries/getCampaignListing";
import { useTranslatedCampaigns } from "../../../hooks/useTranslatedCampaigns";
import { getDateWithFormat, removeEmptyValues } from "../../../utils/commons";
import CampaignFilter from "./CampaignFilter";

export const RelatedCampaign = ({ codeGroupId, linkedCampaigns = [] }) => {
  const { t } = useTranslation();
  const campaignTypeLabelMap = useTranslatedCampaigns();
  const [filters, setFilters] = useState({});
  const campaignFilters = { ...filters, codeGroups: [codeGroupId] };
  const { programTimezone } = useContext(GlobalConfigContext);

  const { data, loading, error } = useQuery(getCampaignListing, {
    skip: !codeGroupId,
    variables: { filter: campaignFilters },
    onError: (err) => {
      console.error("Error fetching campaigns:", err);
    },
  });

  // Some code engagements identify a campaign without returning a code group.
  const campaignList = codeGroupId
    ? (data?.listCampaigns ?? [])
    : linkedCampaigns.filter(
        (campaign) =>
          (!filters.title ||
            campaign.title?.toLowerCase().includes(filters.title.toLowerCase())) &&
          (!filters.types?.length || filters.types.includes(campaign.type)) &&
          (!filters.activeStartDate ||
            (campaign.activeStartDate &&
              new Date(campaign.activeStartDate) >=
                new Date(filters.activeStartDate))) &&
          (!filters.activeEndDate ||
            (campaign.activeEndDate &&
              new Date(campaign.activeEndDate) <= new Date(filters.activeEndDate)))
      );
  const relatedCampaigns = !error
    ? campaignList.map((campaign) => ({
        ...campaign,
        type: campaignTypeLabelMap[campaign.type] || campaign.type,
        activeStartDate: getDateWithFormat(
          campaign.activeStartDate,
          programTimezone
        ),
        activeEndDate: getDateWithFormat(campaign.activeEndDate, programTimezone),
      }))
    : [];

  // Handle filter changes
  const handleFilterChange = (selectedFilter) => {
    const updatedFilters = removeEmptyValues(selectedFilter);
    setFilters(updatedFilters);
  };

  const headers = [
    { header: t("CustomerSupport:CampaignNameColumn"), columnKey: "title" },
    {
      header: t("CustomerSupport:CampaignTitleShortColumn"),
      columnKey: "titleShort",
    },
    {
      header: t("CustomerSupport:CampaignStartDateColumn"),
      columnKey: "activeStartDate",
    },
    {
      header: t("CustomerSupport:CampaignEndDateColumn"),
      columnKey: "activeEndDate",
    },
    { header: t("CustomerSupport:CampaignTypeColumn"), columnKey: "type" },
  ];

  return (
    <div className="list-container">
      <div>
        <CampaignFilter
          handleFilterChange={handleFilterChange}
          filters={campaignFilters}
        />
      </div>
      {loading ? (
        <LoadingSpinner
          size={{ height: "30px", width: "30px" }}
          style={{ margin: "auto" }}
        />
      ) : error ? (
        <p role="alert">{error.message}</p>
      ) : (
        <Table
          headerData={headers}
          rowData={relatedCampaigns}
          noDataLabel={t("CustomerSupport:NoRelatedCampaignsFoundLabel")}
          className="list-table"
        />
      )}
    </div>
  );
};
